<template>
  <div class="landing-page">
    <HeroSection />
    <BrandsSection />
    <HowItWorksSection />
    <FeaturesSection />
    <PlatformsSection />
    <TestimonialsSection />
    <PricingSection />
    <FaqSection />
    <BlogSection />
    <TeamSection />
    <CtaSection />
    <!-- <LandingFooter /> -->
  </div>
</template>

<script setup>
import { onMounted, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import HeroSection from '@/components/landing/HeroSection.vue';
import BrandsSection from '@/components/landing/BrandsSection.vue';
import HowItWorksSection from '@/components/landing/HowItWorksSection.vue';
import FeaturesSection from '@/components/landing/FeaturesSection.vue';
import PlatformsSection from '@/components/landing/PlatformsSection.vue';
import TestimonialsSection from '@/components/landing/TestimonialsSection.vue';
import PricingSection from '@/components/landing/PricingSection.vue';
import FaqSection from '@/components/landing/FaqSection.vue';
import BlogSection from '@/components/landing/BlogSection.vue';
import TeamSection from '@/components/landing/TeamSection.vue';
import CtaSection from '@/components/landing/CtaSection.vue';

const { t } = useI18n();

// Initialize animation with Intersection Observer
onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  // Observe all animate elements
  document.querySelectorAll('.animate:not(.animate--infinite)').forEach(el => {
    observer.observe(el);
  });

  window.smartlook && window.smartlook('track', 'Landing');
});

// Helper function to set or create meta tag
const setMetaTag = (name, content, property = false) => {
  let element = property
    ? document.querySelector(`meta[property="${name}"]`)
    : document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    if (property) {
      element.setAttribute('property', name);
    } else {
      element.setAttribute('name', name);
    }
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

onBeforeMount(() => {
  // Update meta tags for SEO / Open Graph / Social Media
  document.title = t('landing.page_title');

  // Basic meta tags
  setMetaTag('description', t('landing.page_description'));
  setMetaTag('keywords', t('landing.meta_keywords'));

  // Open Graph meta tags
  setMetaTag('og:title', t('landing.og_title'), true);
  setMetaTag('og:description', t('landing.og_description'), true);
  setMetaTag('og:type', 'website', true);
  setMetaTag('og:image', '/og-image.jpg', true);

  // Twitter meta tags
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:title', t('landing.twitter_title'));
  setMetaTag('twitter:description', t('landing.twitter_description'));

  // Schema.org structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Indiefy',
    'url': 'https://indiefy.com',
    'logo': 'https://indiefy.com/logo.png',
    'description': t('landing.page_description'),
    'sameAs': [
      'https://facebook.com/indiefy',
      'https://twitter.com/indiefy',
      'https://instagram.com/indiefy',
      'https://youtube.com/indiefy',
    ],
  });
  document.head.appendChild(script);
});
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";

.landing-page {
  // Global landing page styles
  overflow-x: hidden;
  scroll-behavior: smooth;

  // Fade-in animation for sections when they come into view
  .animate {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }

    &.animate--delay-1 {
      transition-delay: 0.2s;
    }

    &.animate--delay-2 {
      transition-delay: 0.4s;
    }

    &.animate--delay-3 {
      transition-delay: 0.6s;
    }

    &.animate--infinite {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // Override for landing page in dark mode
  .body--dark & {
    background-color: var(--color-background);
    color: var(--color-text);
  }

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>